import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Захист незбереженої чернетки (UIUX patterns/confirmations — «Скасувати ваші зміни?»): перехід за межі сторінки запису (`stay` —
 * шляхи, що лишаються на ній, напр. вкладки) чекає підтвердження, а закриття чи перезавантаження вкладки браузера питає сам браузер.
 * Повертає `release` — пропустити наступний перехід без питання (після видалення запису).
 */
export function useLeaveGuard(dirty: boolean, ask: () => Promise<boolean>, stay: (pathname: string) => boolean): () => void {
  const released = useRef(false);
  const asking = useRef(false);
  const askRef = useRef(ask);
  useEffect(() => { askRef.current = ask; }, [ask]);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    dirty && !released.current && currentLocation.pathname !== nextLocation.pathname && !stay(nextLocation.pathname));
  useEffect(() => {
    if (blocker.state !== 'blocked' || asking.current) return;
    asking.current = true;
    void askRef.current().then((ok) => {
      asking.current = false;
      if (ok) blocker.proceed(); else blocker.reset();
    });
  }, [blocker]);
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
  return () => { released.current = true; };
}
